import { useState, useEffect } from 'react';
import {
  Drawer,
  Tag,
  Typography,
  Divider,
  Select,
  Button,
  Empty,
  DatePicker,
  Input,
  Pagination,
  Spin,
  Modal,
  AutoComplete,
  message as antMessage,
} from 'antd';
import {
  ExperimentOutlined,
  EyeOutlined,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';

const RECIPIENT_PALETTE = {
  sent: { bg: '#dcfce7', fg: '#16a34a', border: '#bbf7d0', label: 'Envoyé' },
  bounced: {
    bg: '#fee2e2',
    fg: '#dc2626',
    border: '#fecaca',
    label: 'Bounce',
  },
  unsubscribed: {
    bg: '#ffedd5',
    fg: '#ea580c',
    border: '#fed7aa',
    label: 'Désabonné',
  },
  failed: {
    bg: '#fef3c7',
    fg: '#b45309',
    border: '#fde68a',
    label: 'Échec SMTP',
  },
};
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import locale from 'antd/es/date-picker/locale/fr_FR';

dayjs.locale('fr');

const { Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

function replaceVars(str, data) {
  if (!str) return str;
  return str.replace(/%recipient\.([a-z_]+)%/gi, (_, key) =>
    data[key] != null ? data[key] : ''
  );
}

export default function GestionBroadcastDetailDrawer({
  open,
  onClose,
  broadcast,
  messageName,
  messageSubject,
  messageSubjectVariants,
  messageHtml,
}) {
  const token = localStorage.getItem('prae.jwt');
  const [testEmail, setTestEmail] = useState('');
  const [testEmails, setTestEmails] = useState([]);
  const [testSending, setTestSending] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [newScheduleDate, setNewScheduleDate] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewContacts, setPreviewContacts] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Recipients section
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientsTotal, setRecipientsTotal] = useState(0);
  const [recipientsCounts, setRecipientsCounts] = useState({});
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsFilter, setRecipientsFilter] = useState('all');
  const [recipientsSearch, setRecipientsSearch] = useState('');
  const [recipientsPage, setRecipientsPage] = useState(1);
  const recipientsPageSize = 50;
  const [bounceModalOpen, setBounceModalOpen] = useState(false);
  const [bounceModalRecipient, setBounceModalRecipient] = useState(null);
  const [bounceHistory, setBounceHistory] = useState([]);
  const [bounceRawHistory, setBounceRawHistory] = useState([]);
  const [bounceHistoryLoading, setBounceHistoryLoading] = useState(false);

  const openBounceDetail = async recipient => {
    setBounceModalRecipient(recipient);
    setBounceModalOpen(true);
    setBounceHistory([]);
    setBounceRawHistory([]);
    setBounceHistoryLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/gestion-broadcasts/recipient-bounces?email=${encodeURIComponent(recipient.email)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await res.json();
      setBounceHistory(data.history || []);
      setBounceRawHistory(data.raw_history || []);
    } catch {
      setBounceHistory([]);
      setBounceRawHistory([]);
    } finally {
      setBounceHistoryLoading(false);
    }
  };

  const fetchRecipients = async () => {
    if (!broadcast?.id) return;
    setRecipientsLoading(true);
    try {
      const params = new URLSearchParams({
        status: recipientsFilter,
        search: recipientsSearch,
        page: recipientsPage,
        pageSize: recipientsPageSize,
      });
      const res = await fetch(
        `${API_URL}/gestion-broadcasts/${broadcast.id}/recipients?${params}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await res.json();
      setRecipients(data.recipients || []);
      setRecipientsTotal(data.total || 0);
      setRecipientsCounts(data.counts || {});
    } catch {
      setRecipients([]);
      setRecipientsTotal(0);
    } finally {
      setRecipientsLoading(false);
    }
  };

  // Charge les destinataires quand la section est ouverte / filtre change
  useEffect(() => {
    if (recipientsOpen && broadcast?.id) {
      fetchRecipients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    recipientsOpen,
    broadcast?.id,
    recipientsFilter,
    recipientsSearch,
    recipientsPage,
  ]);

  // Reset preview when drawer closes
  useEffect(() => {
    if (!open) {
      setPreviewMode(false);
      setPreviewContacts([]);
      setPreviewIndex(0);
      setRecipientsOpen(false);
      setRecipientsPage(1);
      setRecipientsFilter('all');
      setRecipientsSearch('');
    }
  }, [open]);

  // Load test_emails list
  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/test-emails`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(data => setTestEmails(Array.isArray(data) ? data : []))
      .catch(() => setTestEmails([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSchedule = async date => {
    try {
      await fetch(`${API_URL}/gestion-broadcasts/${broadcast.id}/schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          scheduled_start_at: date ? date.toISOString() : null,
        }),
      });
      broadcast.scheduled_start_at = date ? date.toISOString() : null;
      setEditingSchedule(false);
      antMessage.success(
        date ? 'Campagne programmée' : 'Campagne déprogrammée'
      );
    } catch {
      antMessage.error('Erreur programmation');
    }
  };

  const loadPreviewContacts = async () => {
    if (!broadcast?.playlist_id) return;
    setPreviewLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/playlists/${broadcast.playlist_id}/gestion-contacts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            page: 1,
            page_size: 100,
            filter_view: 'active',
          }),
        }
      );
      const json = await res.json();
      setPreviewContacts(json.contacts || []);
      setPreviewIndex(0);
      setPreviewMode(true);
    } catch (err) {
      console.error('[preview] Error:', err);
      antMessage.error(`Erreur chargement contacts : ${err.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail || !broadcast) return;
    setTestSending(true);
    try {
      const token = localStorage.getItem('prae.jwt');
      const res = await fetch(
        `${API_URL}/gestion-broadcasts/${broadcast.id}/test`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ email: testEmail }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      antMessage.success(`Test envoyé à ${testEmail}`);
    } catch (err) {
      antMessage.error(`Erreur : ${err.message}`);
    } finally {
      setTestSending(false);
    }
  };

  if (!broadcast) return null;

  const statusLabel =
    broadcast.status === 'active'
      ? 'En cours'
      : broadcast.status === 'paused'
        ? 'En pause'
        : broadcast.status === 'completed'
          ? 'Terminé'
          : broadcast.status;

  const statusColor =
    broadcast.status === 'active'
      ? 'green'
      : broadcast.status === 'paused'
        ? 'orange'
        : broadcast.status === 'completed'
          ? 'blue'
          : 'default';

  const percent =
    broadcast.total_to_send > 0
      ? Math.round((broadcast.total_sent / broadcast.total_to_send) * 100)
      : 0;

  return (
    <Drawer title={broadcast.name} open={open} onClose={onClose} width={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Status */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Tag
            color={statusColor}
            style={{ fontSize: 13, padding: '2px 10px' }}
          >
            {statusLabel}
          </Tag>
          <Text style={{ fontSize: 13, color: '#64748b' }}>
            Jour {broadcast.current_day} / {broadcast.warmup_total_days}
          </Text>
        </div>

        {/* Progress */}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 4,
            }}
          >
            <Text style={{ fontSize: 13 }}>
              {broadcast.total_sent || 0} / {broadcast.total_to_send || 0}{' '}
              emails envoyés
            </Text>
            <Text strong style={{ fontSize: 13 }}>
              {percent}%
            </Text>
          </div>
          <div
            style={{
              height: 8,
              background: '#f0f0f0',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${Math.min(percent, 100)}%`,
                background:
                  broadcast.status === 'active'
                    ? '#52c41a'
                    : broadcast.status === 'paused'
                      ? '#faad14'
                      : '#1677ff',
                borderRadius: 4,
                transition: 'width 0.3s',
              }}
            />
          </div>
        </div>

        {/* Details */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
          }}
        >
          <div
            style={{
              background: '#fafafa',
              borderRadius: 8,
              padding: '10px 14px',
            }}
          >
            <Text style={{ fontSize: 11, color: '#94a3b8', display: 'block' }}>
              Provider SMTP
            </Text>
            <Text strong style={{ fontSize: 13 }}>
              {broadcast.smtp_provider_name || '–'}
            </Text>
          </div>
          <div
            style={{
              background: '#fafafa',
              borderRadius: 8,
              padding: '10px 14px',
            }}
          >
            <Text style={{ fontSize: 11, color: '#94a3b8', display: 'block' }}>
              Envois aujourd'hui
            </Text>
            <Text strong style={{ fontSize: 13 }}>
              {broadcast.emails_sent_today || 0}
            </Text>
          </div>
          <div
            style={{
              background: broadcast.scheduled_start_at ? '#f0fdf4' : '#fafafa',
              borderRadius: 8,
              padding: '10px 14px',
              border: broadcast.scheduled_start_at
                ? '1px solid #bbf7d0'
                : '1px solid #e5e7eb',
            }}
          >
            <Text
              style={{
                fontSize: 11,
                color: broadcast.scheduled_start_at ? '#16a34a' : '#94a3b8',
                display: 'block',
              }}
            >
              {broadcast.scheduled_start_at ? 'Programmé le' : 'Non programmé'}
            </Text>
            {editingSchedule ? (
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <DatePicker
                  value={newScheduleDate}
                  onChange={setNewScheduleDate}
                  format="DD/MM/YYYY"
                  locale={locale}
                  size="small"
                  disabledDate={c => c && c < dayjs().endOf('day')}
                  style={{ flex: 1 }}
                />
                <Button
                  size="small"
                  type="primary"
                  onClick={() => handleSchedule(newScheduleDate)}
                  disabled={!newScheduleDate}
                >
                  OK
                </Button>
                <Button size="small" onClick={() => setEditingSchedule(false)}>
                  ✕
                </Button>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 2,
                }}
              >
                <Text strong style={{ fontSize: 13 }}>
                  {broadcast.scheduled_start_at
                    ? new Date(broadcast.scheduled_start_at).toLocaleDateString(
                        'fr-FR',
                        {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        }
                      )
                    : '–'}
                </Text>
                <Button
                  size="small"
                  type="link"
                  icon={<CalendarOutlined />}
                  onClick={() => {
                    setNewScheduleDate(
                      broadcast.scheduled_start_at
                        ? dayjs(broadcast.scheduled_start_at)
                        : dayjs().add(1, 'day')
                    );
                    setEditingSchedule(true);
                  }}
                  style={{ padding: 0, fontSize: 11 }}
                >
                  {broadcast.scheduled_start_at ? 'Modifier' : 'Programmer'}
                </Button>
                {broadcast.scheduled_start_at && (
                  <Button
                    size="small"
                    type="link"
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => handleSchedule(null)}
                    style={{ padding: 0, fontSize: 11 }}
                  >
                    Déprogrammer
                  </Button>
                )}
              </div>
            )}
          </div>
          <div
            style={{
              background: '#fff7ed',
              borderRadius: 8,
              padding: '10px 14px',
              border: '1px solid #fed7aa',
            }}
          >
            <Text style={{ fontSize: 11, color: '#c2410c', display: 'block' }}>
              Contacts figés le
            </Text>
            <Text strong style={{ fontSize: 13, color: '#c2410c' }}>
              {new Date(broadcast.created_at).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </div>
        </div>

        <Divider style={{ margin: '4px 0' }} />

        {/* Test */}
        <div>
          <Text
            strong
            style={{ fontSize: 13, marginBottom: 6, display: 'block' }}
          >
            Envoyer un test
          </Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <AutoComplete
              placeholder="Choisir ou saisir un email de test"
              value={testEmail}
              onChange={setTestEmail}
              filterOption={(input, option) =>
                (option?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={testEmails.map(t => ({
                value: t.email,
                label: t.email,
              }))}
              style={{ flex: 1 }}
              allowClear
              onPressEnter={handleTest}
            />
            <Button
              icon={<ExperimentOutlined />}
              onClick={handleTest}
              loading={testSending}
              disabled={!testEmail}
            >
              Tester
            </Button>
          </div>
        </div>

        <Divider style={{ margin: '4px 0' }} />

        {/* Destinataires */}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Text strong style={{ fontSize: 13 }}>
              Destinataires
            </Text>
            <Button
              size="small"
              onClick={() => setRecipientsOpen(o => !o)}
            >
              {recipientsOpen ? 'Masquer' : 'Afficher'}
            </Button>
          </div>
          {recipientsOpen && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <Input
                size="small"
                placeholder="Rechercher un email..."
                prefix={<SearchOutlined />}
                value={recipientsSearch}
                onChange={e => {
                  setRecipientsSearch(e.target.value);
                  setRecipientsPage(1);
                }}
                allowClear
              />
              <div
                style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}
              >
                {[
                  { key: 'all', label: 'Tous' },
                  { key: 'sent', label: 'Envoyés' },
                  { key: 'bounced', label: 'Bounces' },
                  { key: 'unsubscribed', label: 'Désabos' },
                  { key: 'failed', label: 'Échecs SMTP' },
                ].map(opt => {
                  const isActive = recipientsFilter === opt.key;
                  const palette =
                    opt.key === 'all'
                      ? null
                      : RECIPIENT_PALETTE[opt.key];
                  return (
                    <Button
                      key={opt.key}
                      size="small"
                      onClick={() => {
                        setRecipientsFilter(opt.key);
                        setRecipientsPage(1);
                      }}
                      style={{
                        fontSize: 11,
                        ...(isActive && palette
                          ? {
                              background: palette.bg,
                              color: palette.fg,
                              borderColor: palette.border,
                              fontWeight: 600,
                            }
                          : isActive
                            ? {
                                background: '#0f172a',
                                color: '#fff',
                                borderColor: '#0f172a',
                              }
                            : {}),
                      }}
                    >
                      {opt.label} (
                      {recipientsCounts[opt.key] !== undefined
                        ? recipientsCounts[opt.key]
                        : '…'}
                      )
                    </Button>
                  );
                })}
              </div>

              {recipientsLoading ? (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <Spin size="small" />
                </div>
              ) : recipients.length === 0 ? (
                <Empty
                  description="Aucun destinataire"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    maxHeight: 360,
                    overflowY: 'auto',
                    border: '1px solid #f1f5f9',
                    borderRadius: 6,
                    padding: 4,
                  }}
                >
                  {recipients.map(r => {
                    const palette = RECIPIENT_PALETTE[r.status] || {};
                    const cd = r.contact_data || {};
                    const fullName = [cd.civilite, cd.nom, cd.prenom]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <div
                        key={r.email}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 8px',
                          borderRadius: 4,
                          background: '#fff',
                          fontSize: 12,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              color: '#1e293b',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {r.email}
                          </div>
                          {(fullName || r.sent_at) && (
                            <div
                              style={{
                                fontSize: 11,
                                color: '#94a3b8',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {fullName}
                              {fullName && r.sent_at ? ' · ' : ''}
                              {r.sent_at
                                ? new Date(r.sent_at).toLocaleString('fr-FR', {
                                    day: '2-digit',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : ''}
                            </div>
                          )}
                        </div>
                        <Tag
                          onClick={
                            r.status === 'bounced'
                              ? e => {
                                  e.stopPropagation();
                                  openBounceDetail(r);
                                }
                              : undefined
                          }
                          style={{
                            background: palette.bg,
                            color: palette.fg,
                            border: `1px solid ${palette.border}`,
                            borderRadius: 999,
                            fontWeight: 600,
                            fontSize: 10,
                            padding: '1px 8px',
                            margin: 0,
                            cursor:
                              r.status === 'bounced' ? 'pointer' : 'default',
                          }}
                        >
                          {palette.label}
                          {r.bounce_class ? ` · ${r.bounce_class}` : ''}
                          {r.bounce_count > 1 ? ` (${r.bounce_count}×)` : ''}
                        </Tag>
                      </div>
                    );
                  })}
                </div>
              )}

              {recipientsTotal > recipientsPageSize && (
                <Pagination
                  size="small"
                  current={recipientsPage}
                  total={recipientsTotal}
                  pageSize={recipientsPageSize}
                  onChange={p => setRecipientsPage(p)}
                  showSizeChanger={false}
                  style={{ textAlign: 'center' }}
                />
              )}
            </div>
          )}
        </div>

        <Divider style={{ margin: '4px 0' }} />

        {/* Message */}
        <div>
          <Text
            strong
            style={{ fontSize: 13, marginBottom: 8, display: 'block' }}
          >
            Message : {messageName || '–'}
          </Text>
          {messageSubject && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                <strong>Objet :</strong> {messageSubject}
              </div>
              {messageSubjectVariants && messageSubjectVariants.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    marginLeft: 12,
                  }}
                >
                  {messageSubjectVariants.map((v, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#94a3b8' }}>
                      Variante {i + 1} : {v}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Bouton prévisualiser */}
          <Button
            icon={<EyeOutlined />}
            onClick={loadPreviewContacts}
            loading={previewLoading}
            style={{ marginBottom: 8 }}
            type={previewMode ? 'primary' : 'default'}
          >
            {previewMode
              ? 'Prévisualisation active'
              : 'Prévisualiser avec les contacts'}
          </Button>

          {previewMode && previewContacts.length > 0 ? (
            <>
              {/* Navigation */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                  padding: '6px 10px',
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: 8,
                }}
              >
                <Button
                  size="small"
                  icon={<LeftOutlined />}
                  disabled={previewIndex === 0}
                  onClick={() => setPreviewIndex(i => i - 1)}
                />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {previewContacts[previewIndex]?.civilite}{' '}
                    {previewContacts[previewIndex]?.prenom}{' '}
                    {previewContacts[previewIndex]?.nom}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {previewContacts[previewIndex]?.email} — {previewIndex + 1}/
                    {previewContacts.length}
                  </div>
                </div>
                <Button
                  size="small"
                  icon={<RightOutlined />}
                  disabled={previewIndex >= previewContacts.length - 1}
                  onClick={() => setPreviewIndex(i => i + 1)}
                />
              </div>

              {/* Objet rendu */}
              <div
                style={{
                  fontSize: 13,
                  color: '#1e293b',
                  fontWeight: 600,
                  marginBottom: 6,
                  padding: '4px 8px',
                  background: '#fafafa',
                  borderRadius: 6,
                }}
              >
                Objet :{' '}
                {replaceVars(
                  messageSubject,
                  previewContacts[previewIndex] || {}
                )}
              </div>

              {/* HTML rendu */}
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  srcDoc={`<style>body{font-family:Arial,sans-serif;font-size:14px;margin:12px;color:#333}p{margin:0}</style>${replaceVars(messageHtml, previewContacts[previewIndex] || {})}`}
                  sandbox=""
                  title="Prévisualisation"
                  style={{
                    width: '100%',
                    height: 300,
                    border: 'none',
                  }}
                />
              </div>
            </>
          ) : previewMode && previewContacts.length === 0 ? (
            <Empty description="Aucun contact dans cette playlist" />
          ) : messageHtml ? (
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              <iframe
                srcDoc={`<style>body{font-family:Arial,sans-serif;font-size:14px;margin:12px;color:#333}p{margin:0}</style>${messageHtml}`}
                sandbox=""
                title="Aperçu message"
                style={{
                  width: '100%',
                  height: 300,
                  border: 'none',
                }}
              />
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Aucun contenu
            </Text>
          )}
        </div>
      </div>

      <Modal
        title={
          bounceModalRecipient
            ? `Bounce : ${bounceModalRecipient.email}`
            : 'Bounce'
        }
        open={bounceModalOpen}
        onCancel={() => setBounceModalOpen(false)}
        footer={null}
        width={640}
      >
        {bounceModalRecipient && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Détail du dernier bounce */}
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: 12,
                fontSize: 12,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 13,
                  marginBottom: 6,
                  color: '#991b1b',
                }}
              >
                Dernier bounce
                {bounceModalRecipient.bounce_count > 1
                  ? ` (sur ${bounceModalRecipient.bounce_count} au total)`
                  : ''}
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <Row
                  label="Classification"
                  value={bounceModalRecipient.bounce_class}
                />
                <Row
                  label="Cause"
                  value={bounceModalRecipient.bounce_cause}
                />
                <Row
                  label="Code SMTP"
                  value={bounceModalRecipient.bounce_smtp_code}
                />
                <Row
                  label="Diagnostic"
                  value={bounceModalRecipient.bounce_diagnostic}
                  pre
                />
                <Row
                  label="Reçu le"
                  value={
                    bounceModalRecipient.bounce_received_at
                      ? new Date(
                          bounceModalRecipient.bounce_received_at
                        ).toLocaleString('fr-FR')
                      : null
                  }
                />
                <Row
                  label="Domaine émetteur"
                  value={bounceModalRecipient.bounce_sender_domain}
                />
                <Row
                  label="IP sortante"
                  value={bounceModalRecipient.bounce_outbound_ip}
                />
              </div>
            </div>

            {/* Détails techniques bruts (email_bounces) */}
            {bounceRawHistory.length > 0 && (
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 13,
                    marginBottom: 6,
                  }}
                >
                  Détails techniques ({bounceRawHistory.length})
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    maxHeight: 240,
                    overflowY: 'auto',
                  }}
                >
                  {bounceRawHistory.map((h, i) => (
                    <div
                      key={i}
                      style={{
                        border: '1px solid #fde68a',
                        background: '#fffbeb',
                        borderRadius: 6,
                        padding: 8,
                        fontSize: 11,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: 4,
                        }}
                      >
                        <strong>
                          {h.smtp_code ? `SMTP ${h.smtp_code}` : 'Bounce'}
                          {h.action ? ` — ${h.action}` : ''}
                        </strong>
                        <span style={{ color: '#94a3b8' }}>
                          {h.bounce_time
                            ? new Date(h.bounce_time).toLocaleString('fr-FR')
                            : ''}
                        </span>
                      </div>
                      {h.reason && (
                        <div style={{ color: '#475569', marginBottom: 2 }}>
                          Raison : {h.reason}
                        </div>
                      )}
                      {h.diagnostic_code && (
                        <div
                          style={{
                            color: '#475569',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            fontSize: 10,
                            background: '#fff',
                            padding: 4,
                            marginTop: 4,
                            borderRadius: 4,
                            border: '1px solid #fde68a',
                          }}
                        >
                          {h.diagnostic_code}
                        </div>
                      )}
                      {h.reporting_mta && (
                        <div
                          style={{
                            color: '#94a3b8',
                            marginTop: 4,
                            fontSize: 10,
                          }}
                        >
                          MTA : {h.reporting_mta}
                          {h.final_recipient
                            ? ` · ${h.final_recipient}`
                            : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Historique complet */}
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 13,
                  marginBottom: 6,
                }}
              >
                Historique campagnes ({bounceHistory.length})
              </div>
              {bounceHistoryLoading ? (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <Spin size="small" />
                </div>
              ) : bounceHistory.length === 0 ? (
                <Empty
                  description="Aucun historique"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    maxHeight: 280,
                    overflowY: 'auto',
                  }}
                >
                  {bounceHistory.map((h, i) => (
                    <BounceHistoryItem key={i} h={h} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </Drawer>
  );
}

function BounceHistoryItem({ h }) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: 8,
        fontSize: 11,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <strong>{h.classification}</strong>
        <span style={{ color: '#94a3b8' }}>
          {h.received_at
            ? new Date(h.received_at).toLocaleString('fr-FR')
            : ''}
        </span>
      </div>
      {h.broadcast_name && (
        <div style={{ color: '#475569', marginBottom: 2 }}>
          Campagne :{' '}
          <span style={{ color: '#1e293b', fontWeight: 600 }}>
            {h.broadcast_name}
          </span>
        </div>
      )}
      {h.dsn_smtp_code && (
        <div style={{ color: '#475569' }}>
          SMTP : <code>{h.dsn_smtp_code}</code>
        </div>
      )}
      {h.classification_cause && (
        <div style={{ color: '#475569' }}>
          Cause : {h.classification_cause}
        </div>
      )}
      {h.dsn_diagnostic && (
        <div
          style={{
            color: '#475569',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
            fontSize: 10,
            background: '#f9fafb',
            padding: 4,
            marginTop: 4,
            borderRadius: 4,
          }}
        >
          {h.dsn_diagnostic}
        </div>
      )}
      {h.dsn_full && Object.keys(h.dsn_full).length > 0 && (
        <details style={{ marginTop: 6 }}>
          <summary
            style={{ cursor: 'pointer', color: '#475569', fontSize: 11 }}
          >
            Tous les champs DSN ({Object.keys(h.dsn_full).length})
          </summary>
          <pre
            style={{
              background: '#f9fafb',
              padding: 6,
              borderRadius: 4,
              fontSize: 10,
              marginTop: 4,
              maxHeight: 160,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(h.dsn_full, null, 2)}
          </pre>
        </details>
      )}
      {h.raw_excerpt && (
        <div style={{ marginTop: 6 }}>
          <Button
            size="small"
            type="link"
            style={{ padding: 0, fontSize: 11, height: 'auto' }}
            onClick={() => setShowRaw(s => !s)}
          >
            {showRaw ? 'Masquer le mail de bounce' : 'Voir le mail de bounce'}
          </Button>
          {showRaw && (
            <pre
              style={{
                background: '#0f172a',
                color: '#e2e8f0',
                padding: 8,
                borderRadius: 4,
                fontSize: 10,
                marginTop: 4,
                maxHeight: 320,
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {h.raw_excerpt}
            </pre>
          )}
        </div>
      )}
      {(h.sender_domain || h.outbound_ip) && (
        <div style={{ color: '#94a3b8', marginTop: 4, fontSize: 10 }}>
          {h.sender_domain ? `via ${h.sender_domain}` : ''}
          {h.outbound_ip ? ` (${h.outbound_ip})` : ''}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, pre }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ minWidth: 130, color: '#64748b' }}>{label}</div>
      <div
        style={{
          flex: 1,
          color: '#1e293b',
          ...(pre
            ? {
                fontFamily: 'monospace',
                fontSize: 11,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }
            : {}),
        }}
      >
        {value}
      </div>
    </div>
  );
}
